<template>
    <div class="fabricando-v2" :class="{ 'is-pre': estado === 'pre' }">

        <!-- ============ HEADER + KPIs ============ -->
        <div class="dash-header">
            <div class="dash-header-top">
                <div>
                    <div class="dash-eyebrow" :class="{ 'eyebrow-pre': estado === 'pre' }">{{ estado === 'pre' ? 'WAITING · QUEUE' : 'PRODUCTION · LIVE' }}</div>
                    <h2 class="dash-title">{{ estado === 'pre' ? 'Pre-producción' : 'En Producción' }}</h2>
                </div>
                <div class="dash-header-actions">
                    <div class="seg-control seg-control-wide" role="tablist">
                        <button
                            type="button"
                            class="seg-btn"
                            :class="{ active: estado === 'produccion' }"
                            @click="estado = 'produccion'"
                            aria-label="Ver en producción"
                        >
                            <feather-icon icon="ZapIcon" size="14" />
                            <span>En producción</span>
                            <span class="seg-btn-count">{{ countProduccion }}</span>
                        </button>
                        <button
                            type="button"
                            class="seg-btn"
                            :class="{ active: estado === 'pre' }"
                            @click="estado = 'pre'"
                            aria-label="Ver pre-producción"
                        >
                            <feather-icon icon="ClockIcon" size="14" />
                            <span>Pre-producción</span>
                            <span class="seg-btn-count">{{ countPreProduccion }}</span>
                        </button>
                    </div>
                    <div class="seg-control" role="tablist">
                        <button type="button" class="seg-btn" :class="{ active: viewMode === 'list' }" @click="viewMode = 'list'" aria-label="Vista lista">
                            <feather-icon icon="ListIcon" size="14" />
                        </button>
                        <button type="button" class="seg-btn" :class="{ active: viewMode === 'grid' }" @click="viewMode = 'grid'" aria-label="Vista grid">
                            <feather-icon icon="GridIcon" size="14" />
                        </button>
                    </div>
                    <button class="btn-reload" @click="cargarDatos" :disabled="loading" aria-label="Recargar datos">
                        <feather-icon icon="RefreshCwIcon" size="14" :class="{ 'spin': loading }" />
                        <span>Recargar</span>
                    </button>
                </div>
            </div>

            <!-- KPI strip -->
            <div class="kpi-strip">
                <div class="kpi-card">
                    <div class="kpi-label">REFERENCIAS</div>
                    <div class="kpi-value">{{ productosVista.length }}</div>
                    <div class="kpi-foot">
                        <span class="dot" :class="estado === 'pre' ? 'dot-amber' : 'dot-blue'"></span>
                        <span>{{ estado === 'pre' ? 'esperando corte' : 'en fabricación' }}</span>
                    </div>
                </div>
                <div class="kpi-card">
                    <div class="kpi-label">UNIDADES TOTALES</div>
                    <div class="kpi-value">{{ totalUnidades }}</div>
                    <div class="kpi-foot">
                        <span class="dot dot-orange"></span>
                        <span>{{ estado === 'pre' ? 'uds por cortar' : 'uds en taller' }}</span>
                    </div>
                </div>
                <div class="kpi-card">
                    <div class="kpi-label">COLOR TOP</div>
                    <div class="kpi-value kpi-value-sm">
                        <span class="color-chip lg" :style="getColorStyle(topColor.name)"></span>
                        {{ topColor.name || '—' }}
                    </div>
                    <div class="kpi-foot">
                        <span>{{ topColor.count }} uds</span>
                    </div>
                </div>
                <div class="kpi-card">
                    <div class="kpi-label">ÚLTIMA ACTUALIZACIÓN</div>
                    <div class="kpi-value kpi-value-sm">{{ ultimaActualizacionGlobal || '—' }}</div>
                    <div class="kpi-foot">
                        <span class="dot dot-green pulse"></span>
                        <span>tiempo real</span>
                    </div>
                </div>
            </div>

            <!-- Filtros densos -->
            <div class="filters-bar">
                <div class="filter-search">
                    <feather-icon icon="SearchIcon" size="14" class="filter-search-icon" />
                    <input
                        v-model="filtro"
                        type="text"
                        placeholder="Buscar por nombre, SKU o color..."
                        class="filter-input"
                    />
                    <button
                        v-if="filtro"
                        type="button"
                        class="filter-clear"
                        @click="filtro = ''"
                        aria-label="Limpiar filtro"
                    >
                        <feather-icon icon="XIcon" size="12" />
                    </button>
                </div>

                <div class="filter-results">
                    <span class="filter-results-num">{{ productosFiltrados.length }}</span>
                    <span class="filter-results-txt">de {{ productos.length }}</span>
                </div>
            </div>
        </div>

        <!-- ============ LOADING SKELETON ============ -->
        <div v-if="loading" class="skeleton-grid">
            <div v-for="n in 7" :key="n" class="skeleton-card">
                <div class="skeleton-img"></div>
                <div class="skeleton-line w-80"></div>
                <div class="skeleton-line w-60"></div>
                <div class="skeleton-line w-40"></div>
            </div>
        </div>

        <!-- ============ GRID VIEW ============ -->
        <div
            v-else-if="viewMode === 'grid' && productosFiltrados.length > 0"
            class="prod-grid"
        >
            <article
                v-for="(prod, idx) in productosFiltrados"
                :key="prod.sku"
                class="prod-card"
                :class="{ 'prod-card--negative-stock': Number(prod.stockActual) <= 0 }"
                tabindex="0"
                role="button"
                :aria-label="'Ver historial de ' + prod.nombre"
                @click="verHistorial(prod)"
                @keyup.enter="verHistorial(prod)"
            >
                <div class="prod-card-header">
                    <span class="prod-idx">#{{ String(idx + 1).padStart(3, '0') }}</span>
                    <span class="prod-qty-badge">
                        <span class="prod-qty-num">{{ prod.cantidad }}</span>
                        <span class="prod-qty-uds">UDS</span>
                    </span>
                </div>

                <div class="prod-card-img">
                    <img v-if="prod.imagen" :src="prod.imagen" :alt="prod.nombre" loading="lazy" />
                    <div v-else class="prod-img-placeholder">
                        <feather-icon icon="ImageIcon" size="28" />
                    </div>
                </div>

                <div class="prod-card-body">
                    <h3 class="prod-name">{{ prod.nombre }}</h3>

                    <div class="prod-meta">
                        <div class="meta-row">
                            <span class="meta-k">SKU</span>
                            <span class="meta-v meta-mono" :class="skuClass(prod.sku)">{{ prod.sku }}</span>
                        </div>
                        <div class="meta-row">
                            <span class="meta-k">COLOR</span>
                            <span class="meta-v">
                                <span class="color-chip" :style="getColorStyle(prod.color)"></span>
                                {{ prod.color }}
                            </span>
                        </div>
                        <div class="meta-row">
                            <span class="meta-k">UPDATE</span>
                            <span class="meta-v meta-mono">{{ prod.ultimaActualizacion ? formatFecha(prod.ultimaActualizacion) : '—' }}</span>
                        </div>
                        <div class="meta-row">
                            <span class="meta-k">ESTIMADO</span>
                            <span class="meta-v">
                                <span
                                    class="eta-badge"
                                    :class="getEtaBadgeClass(prod)"
                                >
                                    {{ getEtaLabel(prod) }}
                                </span>
                            </span>
                        </div>
                        <div class="meta-row">
                            <span class="meta-k">STOCK</span>
                            <span class="meta-v">
                                <span class="stock-badge" :class="getStockBadgeClass(prod.stockActual)">
                                    {{ formatStock(prod.stockActual) }}
                                </span>
                            </span>
                        </div>
                    </div>
                </div>

                <div class="prod-card-footer">
                    <span @click.stop="verHistorial(prod)">Ver historial</span>
                    <button class="btn-preorder" :class="{ 'btn-preorder--active': prod.shipDate }" @click.stop="abrirPreorder(prod)" :title="prod.shipDate || 'Actualizar pre-order'">
                        <i class="fa-solid fa-tag"></i>
                    </button>
                </div>
            </article>
        </div>

        <!-- ============ LIST VIEW ============ -->
        <div
            v-else-if="viewMode === 'list' && productosFiltrados.length > 0"
            class="list-wrap"
        >
            <table class="list-table">
                <thead>
                    <tr>
                        <th class="lt-check" @click.stop><input type="checkbox" :checked="allSelected" @change="toggleSelectAll" class="list-checkbox" /></th>
                        <th class="lt-img"></th>
                        <th>PRODUCTO</th>
                        <th>SKU</th>
                        <th>COLOR</th>
                        <th class="lt-center">UDS</th>
                        <th class="lt-center">STOCK</th>
                        <th>ESTIMADO</th>
                        <th>ÚLTIMA ACT.</th>
                        <th class="lt-right"></th>
                    </tr>
                </thead>
                <tbody>
                    <tr
                        v-for="prod in productosFiltrados"
                        :key="'list-' + prod.sku"
                        :class="{ 'lt-selected': selectedSkus.includes(prod.sku), 'tr-negative-stock': Number(prod.stockActual) <= 0 }"
                        tabindex="0"
                        @click="toggleSelect(prod.sku)"
                    >
                        <td class="lt-check" @click.stop><input type="checkbox" :value="prod.sku" v-model="selectedSkus" class="list-checkbox" /></td>
                        <td class="td-img">
                            <img v-if="prod.imagen" :src="prod.imagen" :alt="prod.nombre" loading="lazy" />
                            <div v-else class="td-img-ph"><feather-icon icon="ImageIcon" size="14" /></div>
                        </td>
                        <td class="td-name">{{ prod.nombre }}</td>
                        <td class="mono td-sku">{{ prod.sku }}</td>
                        <td class="td-color">
                            <span class="color-chip" :style="getColorStyle(prod.color)"></span>
                            <span class="td-color-txt">{{ prod.color }}</span>
                        </td>
                        <td class="td-center">
                            <span class="qty-pill">{{ prod.cantidad }}</span>
                        </td>
                        <td class="td-center">
                            <span class="stock-badge" :class="getStockBadgeClass(prod.stockActual)">{{ formatStock(prod.stockActual) }}</span>
                        </td>
                        <td>
                            <span class="eta-badge" :class="getEtaBadgeClass(prod)">{{ getEtaLabel(prod) }}</span>
                        </td>
                        <td class="mono td-date">{{ prod.ultimaActualizacion ? formatFecha(prod.ultimaActualizacion) : '—' }}</td>
                        <td class="td-right td-actions" @click.stop>
                            <button class="btn-hist" @click="verHistorial(prod)" v-b-tooltip.hover.top="'Ver historial'">
                                <feather-icon icon="ClockIcon" size="13" />
                            </button>
                            <button class="btn-preorder btn-preorder--table" :class="{ 'btn-preorder--active': prod.shipDate }" @click="abrirPreorder(prod)" v-b-tooltip.hover.top="prod.shipDate ? ('Pre-order: ' + prod.shipDate) : 'Actualizar pre-order'">
                                <i class="fa-solid fa-tag"></i>
                            </button>
                        </td>
                    </tr>
                </tbody>
            </table>
        </div>

        <!-- ============ TABLE VIEW ============ -->
        <div
            v-if="!loading && viewMode === 'table' && productosFiltrados.length > 0"
            class="table-wrap"
        >
            <table class="prod-table">
                <thead>
                    <tr>
                        <th class="th-img"></th>
                        <th>PRODUCTO</th>
                        <th>SKU</th>
                        <th>COLOR</th>
                        <th class="th-center">UDS</th>
                        <th class="th-center">STOCK</th>
                        <th>ESTIMADO</th>
                        <th>ÚLTIMA ACT.</th>
                        <th class="th-right"></th>
                    </tr>
                </thead>
                <tbody>
                    <tr
                        v-for="prod in productosFiltrados"
                        :key="prod.sku"
                        :class="{ 'tr-negative-stock': Number(prod.stockActual) <= 0 }"
                        tabindex="0"
                        @click="verHistorial(prod)"
                        @keyup.enter="verHistorial(prod)"
                    >
                        <td class="td-img">
                            <img v-if="prod.imagen" :src="prod.imagen" :alt="prod.nombre" loading="lazy" />
                            <div v-else class="td-img-ph"><feather-icon icon="ImageIcon" size="14" /></div>
                        </td>
                        <td class="td-name">{{ prod.nombre }}</td>
                        <td class="mono td-sku">{{ prod.sku }}</td>
                        <td class="td-color">
                            <span class="color-chip" :style="getColorStyle(prod.color)"></span>
                            <span class="td-color-txt">{{ prod.color }}</span>
                        </td>
                        <td class="td-center">
                            <span class="qty-pill">{{ prod.cantidad }}</span>
                        </td>
                        <td class="td-center">
                            <span class="stock-badge" :class="getStockBadgeClass(prod.stockActual)">
                                {{ formatStock(prod.stockActual) }}
                            </span>
                        </td>
                        <td>
                            <span class="eta-badge" :class="getEtaBadgeClass(prod)">{{ getEtaLabel(prod) }}</span>
                        </td>
                        <td class="mono td-date">{{ prod.ultimaActualizacion ? formatFecha(prod.ultimaActualizacion) : '—' }}</td>
                        <td class="td-right td-actions">
                            <button class="btn-preorder btn-preorder--table" :class="{ 'btn-preorder--active': prod.shipDate }" @click.stop="abrirPreorder(prod)" :title="prod.shipDate || 'Pre-order'">
                                <i class="fa-solid fa-tag"></i>
                            </button>
                            <feather-icon icon="ChevronRightIcon" size="14" class="td-chev" />
                        </td>
                    </tr>
                </tbody>
            </table>
        </div>

        <!-- ============ EMPTY ============ -->
        <div v-if="!loading && productosFiltrados.length === 0 && productos.length > 0" class="empty-state">
            <feather-icon icon="SearchIcon" size="32" />
            <p>No hay productos que coincidan con "<b>{{ filtro }}</b>"</p>
            <button class="btn-reload" @click="filtro = ''">Limpiar filtro</button>
        </div>

        <div v-if="!loading && productos.length === 0" class="empty-state empty-success">
            <feather-icon icon="CheckCircleIcon" size="32" />
            <p>{{ estado === 'pre' ? 'No hay productos en pre-producción' : 'No hay productos en fabricación' }}</p>
        </div>

        <!-- ============ FLOATING ACTION BAR ============ -->
        <transition name="fab-slide">
            <div v-if="selectedSkus.length > 0" class="fab-bar">
                <div class="fab-bar-left">
                    <span class="fab-count">{{ selectedSkus.length }}</span>
                    <span class="fab-label">seleccionado(s)</span>
                    <button class="fab-clear" @click="clearSelection">
                        <feather-icon icon="XIcon" size="12" />
                        Deseleccionar
                    </button>
                </div>
                <button class="fab-action" @click="abrirBulkPreorder">
                    <i class="fa-solid fa-tag"></i>
                    Actualizar Pre-Order
                </button>
            </div>
        </transition>

        <!-- ============ MODAL PRE-ORDER ============ -->
        <b-modal ref="modal-preorder" title="Actualizar Pre-Order" size="md" hide-footer centered modal-class="modal-preorder-v2">
            <div class="preorder-modal" v-if="preorderProd">
                <div class="preorder-info">
                    <span class="preorder-sku">{{ preorderProd.sku }}</span>
                    <span class="preorder-name">{{ preorderProd.nombre }}</span>
                </div>
                <div class="preorder-field">
                    <label class="preorder-label">Texto del metafield <code>custom.ship_date</code></label>
                    <input v-model="preorderText" class="preorder-input" placeholder="PRE-ORDER: Ships by September 4th*" />
                </div>
                <div class="preorder-preview">
                    <span class="preorder-preview-label">Vista previa:</span>
                    <span class="preorder-preview-text">{{ preorderText || '(vacio)' }}</span>
                </div>
                <div class="preorder-actions">
                    <button class="btn-preorder-cancel" @click="$refs['modal-preorder'].hide()">Cancelar</button>
                    <button class="btn-preorder-clear" @click="enviarPreorder('')" :disabled="preorderSaving">
                        <i class="fa-solid fa-eraser"></i> Limpiar metafield
                    </button>
                    <button class="btn-preorder-save" @click="enviarPreorder(preorderText)" :disabled="preorderSaving || !preorderText">
                        <i v-if="preorderSaving" class="fa-solid fa-spinner fa-spin"></i>
                        <i v-else class="fa-solid fa-paper-plane"></i>
                        Actualizar en Shopify
                    </button>
                </div>
            </div>
        </b-modal>

        <!-- ============ MODAL BULK PRE-ORDER ============ -->
        <b-modal ref="modal-bulk-preorder" title="Pre-Order Masivo" size="md" hide-footer centered modal-class="modal-preorder-v2">
            <div class="preorder-modal">
                <div class="preorder-info">
                    <span class="preorder-sku">{{ selectedSkus.length }} productos</span>
                    <span class="preorder-name">Se aplicará a todos los seleccionados</span>
                </div>
                <div class="preorder-field">
                    <label class="preorder-label">Texto del metafield <code>custom.ship_date</code></label>
                    <input v-model="bulkPreorderText" class="preorder-input" placeholder="PRE-ORDER: Ships by September 4th*" />
                </div>
                <div class="preorder-preview">
                    <span class="preorder-preview-label">Vista previa:</span>
                    <span class="preorder-preview-text">{{ bulkPreorderText || '(vacio)' }}</span>
                </div>
                <div class="preorder-actions">
                    <button class="btn-preorder-cancel" @click="$refs['modal-bulk-preorder'].hide()">Cancelar</button>
                    <button class="btn-preorder-clear" @click="enviarBulkPreorder('')" :disabled="bulkPreorderSaving">
                        <i class="fa-solid fa-eraser"></i> Limpiar todos
                    </button>
                    <button class="btn-preorder-save" @click="enviarBulkPreorder(bulkPreorderText)" :disabled="bulkPreorderSaving || !bulkPreorderText">
                        <i v-if="bulkPreorderSaving" class="fa-solid fa-spinner fa-spin"></i>
                        <i v-else class="fa-solid fa-paper-plane"></i>
                        Actualizar {{ selectedSkus.length }} en Shopify
                    </button>
                </div>
            </div>
        </b-modal>

        <!-- ============ MODAL HISTORIAL ============ -->
        <b-modal ref="modal-historial" :title="modalTitulo" size="lg" hide-footer centered modal-class="modal-historial-v2">
            <div v-if="historialLoading" class="hist-loading">
                <b-spinner variant="primary" />
            </div>
            <div v-if="!historialLoading && historialNotas.length > 0" class="hist-notas">
                <div class="hist-notas-title"><i class="fa-solid fa-pause"></i> Pausado actualmente</div>
                <div
                    v-for="(n, idx) in historialNotas"
                    :key="'nota-' + idx"
                    class="hist-nota"
                >
                    <div class="hist-nota-head">
                        <span class="hist-tag tag-pause">PAUSADO</span>
                        <span class="hist-pedido mono">{{ n.pedido }}</span>
                        <span class="hist-fecha mono">{{ n.fecha ? formatFecha(n.fecha) : '' }}</span>
                    </div>
                    <div v-if="n.nota" class="hist-nota-text"><i class="fa-regular fa-comment"></i> {{ n.nota }}</div>
                </div>
            </div>
            <div v-if="!historialLoading && historialData.length > 0" class="hist-list">
                <div
                    v-for="(item, i) in historialData"
                    :key="i"
                    class="hist-item"
                >
                    <div class="hist-item-left">
                        <span class="hist-tag" :class="{ 'tag-cut': item.columna === 'cantidadCorte', 'tag-stitch': item.columna === 'cantidadMano', 'tag-pause': item.columna === 'pausa', 'tag-resume': item.columna === 'reanudado' }">
                            {{ item.columna === 'cantidadCorte' ? 'CORTE' : item.columna === 'cantidadMano' ? 'APARADO' : item.columna === 'pausa' ? 'PAUSADO' : 'REANUDADO' }}
                        </span>
                        <span class="hist-pedido mono">{{ item.pedido }}</span>
                    </div>
                    <div class="hist-item-right">
                        <span v-if="item.cantidad !== 0" class="hist-qty">{{ item.cantidad > 0 ? '+' : '' }}{{ item.cantidad }}</span>
                        <span class="hist-fecha mono">{{ formatFecha(item.fecha) }}</span>
                    </div>
                </div>
            </div>
            <div v-if="!historialLoading && historialData.length === 0 && historialNotas.length === 0" class="hist-empty">No hay historial para este producto</div>
        </b-modal>
    </div>
</template>

<script>
import axios from '@axios'

let shopifyCache = null

export default {
    data() {
        return {
            loading: false,
            filtro: '',
            orden: 'estimated',
            viewMode: 'list',
            estado: 'produccion', // 'produccion' | 'pre'
            productos: [],
            historialProducto: {},
            historialData: [],
            historialNotas: [],
            historialLoading: false,
            preorderProd: null,
            preorderText: '',
            preorderSaving: false,
            selectedSkus: [],
            bulkPreorderText: '',
            bulkPreorderSaving: false,
        }
    },
    computed: {
        productosVista() {
            // Filtra los productos según el estado seleccionado y mapea la cantidad activa
            const key = this.estado === 'pre' ? 'totalPreProduccion' : 'totalFabricando'
            return this.productos
                .filter(p => Number(p[key]) > 0)
                .map(p => ({ ...p, cantidad: Number(p[key]) || 0 }))
        },
        countProduccion() {
            return this.productos.filter(p => Number(p.totalFabricando) > 0).length
        },
        countPreProduccion() {
            return this.productos.filter(p => Number(p.totalPreProduccion) > 0).length
        },
        productosFiltrados() {
            let list = this.productosVista
            if (this.filtro) {
                const term = this.filtro.toLowerCase()
                list = list.filter(p =>
                    (p.nombre && p.nombre.toLowerCase().includes(term)) ||
                    (p.sku && p.sku.toLowerCase().includes(term)) ||
                    (p.color && p.color.toLowerCase().includes(term))
                )
            }
            const sorted = list.slice()
            switch (this.orden) {
                case 'estimated':
                    sorted.sort((a, b) => {
                        var aTag = a.shipDate ? 1 : 0
                        var bTag = b.shipDate ? 1 : 0
                        if (aTag !== bTag) return bTag - aTag
                        var aEta = this.getEstimatedDate(a)
                        var bEta = this.getEstimatedDate(b)
                        var aHas = aEta ? 1 : 0
                        var bHas = bEta ? 1 : 0
                        if (aHas !== bHas) return bHas - aHas
                        if (aHas && bHas) return aEta.getTime() - bEta.getTime()
                        return new Date(b.ultimaActualizacion || 0) - new Date(a.ultimaActualizacion || 0)
                    })
                    break
                case 'qty-desc':
                    sorted.sort((a, b) => (b.cantidad || 0) - (a.cantidad || 0))
                    break
                case 'qty-asc':
                    sorted.sort((a, b) => (a.cantidad || 0) - (b.cantidad || 0))
                    break
                case 'name':
                    sorted.sort((a, b) => (a.nombre || '').localeCompare(b.nombre || ''))
                    break
                case 'recent':
                default:
                    sorted.sort((a, b) => new Date(b.ultimaActualizacion || 0) - new Date(a.ultimaActualizacion || 0))
            }
            return sorted
        },
        modalTitulo() {
            if (!this.historialProducto || !this.historialProducto.nombre) return ''
            return this.historialProducto.nombre + ' · ' + this.historialProducto.sku
        },
        totalUnidades() {
            const total = this.productosFiltrados.reduce((acc, p) => acc + (Number(p.cantidad) || 0), 0)
            return total.toLocaleString('es-ES')
        },
        topColor() {
            const map = {}
            this.productosVista.forEach(p => {
                const c = (p.color || 'N/A').toUpperCase()
                map[c] = (map[c] || 0) + (Number(p.cantidad) || 0)
            })
            let top = { name: '', count: 0 }
            Object.keys(map).forEach(k => {
                if (map[k] > top.count) top = { name: k, count: map[k] }
            })
            return top
        },
        ultimaActualizacionGlobal() {
            if (!this.productosVista.length) return ''
            const fechas = this.productosVista
                .map(p => p.ultimaActualizacion)
                .filter(Boolean)
                .map(f => new Date(f).getTime())
            if (!fechas.length) return ''
            return this.formatFecha(new Date(Math.max.apply(null, fechas)))
        },
        allSelected() {
            if (!this.productosFiltrados.length) return false
            return this.productosFiltrados.every(p => this.selectedSkus.includes(p.sku))
        },
        selectedProducts() {
            return this.productosFiltrados.filter(p => this.selectedSkus.includes(p.sku))
        },
    },
    watch: {
        viewMode() { this.selectedSkus = [] },
        estado() { this.selectedSkus = [] },
        filtro() { this.selectedSkus = [] },
    },
    methods: {
        toggleSelectAll() {
            if (this.allSelected) {
                this.selectedSkus = []
            } else {
                this.selectedSkus = this.productosFiltrados.map(p => p.sku)
            }
        },
        clearSelection() {
            this.selectedSkus = []
        },
        toggleSelect(sku) {
            var idx = this.selectedSkus.indexOf(sku)
            if (idx >= 0) {
                this.selectedSkus.splice(idx, 1)
            } else {
                this.selectedSkus.push(sku)
            }
        },
        abrirBulkPreorder() {
            const firstWithEta = this.selectedProducts.find(p => this.getEstimatedDate(p))
            if (firstWithEta) {
                const estimated = this.getEstimatedDate(firstWithEta)
                const months = ['January','February','March','April','May','June','July','August','September','October','November','December']
                const day = estimated.getDate()
                const suffix = day === 1 || day === 21 || day === 31 ? 'st' : day === 2 || day === 22 ? 'nd' : day === 3 || day === 23 ? 'rd' : 'th'
                this.bulkPreorderText = `PRE-ORDER: Ships by ${months[estimated.getMonth()]} ${day}${suffix}*`
            } else {
                this.bulkPreorderText = 'PRE-ORDER: Ships by *'
            }
            this.bulkPreorderSaving = false
            this.$refs['modal-bulk-preorder'].show()
        },
        async enviarBulkPreorder(text) {
            this.bulkPreorderSaving = true
            const skus = [...this.selectedSkus]
            let successCount = 0
            let errorCount = 0

            for (const sku of skus) {
                try {
                    await axios.put('/shopify/products/metafield', { sku, metafieldValue: text })
                    const prod = this.productos.find(p => p.sku === sku)
                    if (prod) prod.shipDate = text
                    successCount++
                } catch (e) {
                    console.error('Error bulk updating metafield for', sku, e)
                    errorCount++
                }
            }

            this.bulkPreorderSaving = false
            this.$refs['modal-bulk-preorder'].hide()
            this.selectedSkus = []

            this.$bvToast.toast(
                successCount + ' producto(s) actualizado(s)' + (errorCount > 0 ? ', ' + errorCount + ' error(es)' : ''),
                { title: 'Pre-Order Masivo', variant: errorCount > 0 ? 'warning' : 'success', solid: true, autoHideDelay: 4000 }
            )
        },
        async cargarDatos() {
            this.loading = true
            try {
                const shopifyPromise = shopifyCache
                    ? Promise.resolve(shopifyCache)
                    : axios.get('/articulos/shopify').then(r => { shopifyCache = r.data; return r.data })

                const [resFabricando, shopifyData, productionWeeksData, shipDatesData] = await Promise.all([
                    axios.get('/taller/pedido/vero/fabricando'),
                    shopifyPromise,
                    axios.get('/sku/production-weeks'),
                    axios.get('/shopify/products/ship-dates').catch(() => ({ data: {} })),
                ])

                const imagesBySku = {}
                const titlesBySku = {}
                const stockBySku = {}
                const productionWeeksBySku = {}
                const draftSkus = new Set()
                const activeSkus = new Set()

                productionWeeksData.data.forEach(item => {
                    if (item.skuSingle) productionWeeksBySku[item.skuSingle] = item.productionWeeks
                })

                shopifyData.forEach(product => {
                    const isDraft = product.status === 'draft'
                    const productImage = product.image && product.image.src || ''
                    const imagesById = {}
                    if (product.images) {
                        product.images.forEach(img => {
                            imagesById[img.id] = img.src
                        })
                    }
                    if (product.variants) {
                        product.variants.forEach(variant => {
                            if (!variant.sku) return
                            if (isDraft) {
                                draftSkus.add(variant.sku)
                                return
                            }
                            activeSkus.add(variant.sku)
                            imagesBySku[variant.sku] = (variant.image_id && imagesById[variant.image_id]) || productImage
                            titlesBySku[variant.sku] = product.title
                            stockBySku[variant.sku] = Number(variant.inventory_quantity) || 0
                        })
                    }
                })

                const shipDatesBySku = shipDatesData.data || {}

                this.productos = resFabricando.data
                    .filter(item => !(draftSkus.has(item.sku) && !activeSkus.has(item.sku)))
                    .map(item => {
                    const lastDash = item.sku.lastIndexOf('-')
                    const raiz = lastDash >= 0 ? item.sku.substring(0, lastDash) : item.sku
                    const color = lastDash >= 0 ? item.sku.substring(lastDash + 1) : ''
                    const productionWeeks = typeof productionWeeksBySku[raiz] !== 'undefined'
                        ? Number(productionWeeksBySku[raiz])
                        : null
                    return {
                        sku: item.sku,
                        nombre: titlesBySku[item.sku] || raiz,
                        imagen: imagesBySku[item.sku] || '',
                        color: color,
                        stockActual: typeof stockBySku[item.sku] !== 'undefined' ? stockBySku[item.sku] : 0,
                        totalFabricando: Number(item.totalFabricando) || 0,
                        totalPreProduccion: Number(item.totalPreProduccion) || 0,
                        ultimaActualizacion: item.ultimaActualizacion || null,
                        ultimaFechaCorte: item.ultimaFechaCorte || null,
                        paused: Boolean(Number(item.paused) || 0),
                        pausedAt: item.pausedAt || null,
                        resumedAt: item.resumedAt || null,
                        productionWeeks,
                        shipDate: shipDatesBySku[item.sku] || '',
                    }
                })
            } catch (e) {
                console.error('Error cargando datos:', e)
            } finally {
                this.loading = false
            }
        },
        async verHistorial(prod) {
            this.historialProducto = prod
            this.historialData = []
            this.historialNotas = []
            this.historialLoading = true
            this.$refs['modal-historial'].show()

            try {
                const res = await axios.get('/taller/pedido/vero/historial/sku/' + encodeURIComponent(prod.sku))
                if (Array.isArray(res.data)) {
                    this.historialData = res.data
                    this.historialNotas = []
                } else {
                    this.historialData = res.data.historial || []
                    this.historialNotas = res.data.notas || []
                }
            } catch (e) {
                console.error('Error cargando historial:', e)
            } finally {
                this.historialLoading = false
            }
        },
        abrirPreorder(prod) {
            this.preorderProd = prod
            this.preorderSaving = false

            // Generate default text with estimated date
            const estimated = this.getEstimatedDate(prod)
            if (estimated) {
                const months = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December']
                const day = estimated.getDate()
                const suffix = day === 1 || day === 21 || day === 31 ? 'st' : day === 2 || day === 22 ? 'nd' : day === 3 || day === 23 ? 'rd' : 'th'
                this.preorderText = `PRE-ORDER: Ships by ${months[estimated.getMonth()]} ${day}${suffix}*`
            } else {
                this.preorderText = 'PRE-ORDER: Ships by *'
            }

            this.$refs['modal-preorder'].show()
        },
        async enviarPreorder(text) {
            if (!this.preorderProd) return
            this.preorderSaving = true

            try {
                await axios.put('/shopify/products/metafield', {
                    sku: this.preorderProd.sku,
                    metafieldValue: text,
                })

                // Actualizar localmente
                const prod = this.productos.find(p => p.sku === this.preorderProd.sku)
                if (prod) prod.shipDate = text

                this.$bvToast.toast(text ? 'Metafield actualizado en Shopify' : 'Metafield limpiado', {
                    title: 'Pre-Order',
                    variant: 'success',
                    solid: true,
                    autoHideDelay: 3000,
                })

                this.$refs['modal-preorder'].hide()
            } catch (e) {
                console.error('Error actualizando metafield:', e)
                this.$bvToast.toast('Error al actualizar en Shopify', {
                    title: 'Error',
                    variant: 'danger',
                    solid: true,
                })
            } finally {
                this.preorderSaving = false
            }
        },
        formatFecha(fecha) {
            const d = new Date(fecha)
            const dia = String(d.getDate()).padStart(2, '0')
            const mes = String(d.getMonth() + 1).padStart(2, '0')
            const hora = String(d.getHours()).padStart(2, '0')
            const min = String(d.getMinutes()).padStart(2, '0')
            return dia + '/' + mes + ' ' + hora + ':' + min
        },
        formatFechaCorta(fecha) {
            const d = new Date(fecha)
            const dia = String(d.getDate()).padStart(2, '0')
            const mes = String(d.getMonth() + 1).padStart(2, '0')
            return dia + '/' + mes
        },
        getEstimatedDate(prod) {
            if (!prod || !prod.productionWeeks) return null
            const baseDate = prod.resumedAt || prod.ultimaFechaCorte
            if (!baseDate) return null
            const estimated = new Date(baseDate)
            estimated.setDate(estimated.getDate() + (Number(prod.productionWeeks) * 7))
            return estimated
        },
        getEtaBadgeClass(prod) {
            if (prod && prod.paused) return 'eta-badge--paused'
            if (!prod || !prod.productionWeeks) return 'eta-badge--missing'
            return 'eta-badge--set'
        },
        getStockBadgeClass(stock) {
            if (Number(stock) <= 0) return 'stock-badge--negative'
            return 'stock-badge--positive'
        },
        formatStock(stock) {
            return Number(stock || 0)
        },
        getEtaLabel(prod) {
            if (prod && prod.paused) return 'Pausado'
            if (!prod || !prod.productionWeeks) return 'Sin asignar'
            const estimatedDate = this.getEstimatedDate(prod)
            if (!estimatedDate) return 'Sin fecha de corte'
            return `${prod.productionWeeks} sem · ${this.formatFechaCorta(estimatedDate)}`
        },
        skuClass(sku) {
            if (!sku) return ''
            const len = sku.length
            if (len > 32) return 'sku-xxs'
            if (len > 28) return 'sku-xs'
            if (len > 22) return 'sku-sm'
            return ''
        },
        getColorStyle(color) {
            const colores = {
                TAN: '#D2691E', BLACK: '#333', BLK: '#333', NAVY: '#1B2A4A',
                BROWN: '#6B3A2A', GREY: '#808080', GREEN: '#2E5E3F', BURGUNDY: '#722F37',
                BLUE: '#1E3A5F', CAMEL: '#C19A6B', COGNAC: '#9A463D', WHISKY: '#D4A24E',
                DB: '#4A2F1B', OLIVE: '#556B2F', MOCHA: '#6B4226', ARMY: '#4B5320',
                CAOBA: '#6B2A2A', RED: '#B22222', PINK: '#D4618C', WHITE: '#f0f0f0',
            }
            return { background: colores[(color || '').toUpperCase()] || '#eee' }
        },
    },
    mounted() {
        this.cargarDatos()
    },
}
</script>

<style lang="scss" scoped>
@import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@300;400;500;600;700;800&family=JetBrains+Mono:wght@400;500;600;700&display=swap');

// ====== Design tokens (Data-Dense Dashboard) ======
$primary: #3B82F6;
$primary-soft: #60A5FA;
$cta: #F97316;
$amber: #F59E0B;
$amber-soft: #FBBF24;
$bg: #F8FAFC;
$surface: #FFFFFF;
$border: #E2E8F0;
$text: #1E293B;
$text-mute: #64748B;
$text-soft: #94A3B8;

.fabricando-v2 {
    font-family: 'Plus Jakarta Sans', -apple-system, BlinkMacSystemFont, sans-serif;
    color: $text;
    padding-bottom: 48px;

    .mono { font-family: 'JetBrains Mono', monospace; font-feature-settings: "tnum", "zero"; }
}

// ====== HEADER ======
.dash-header {
    background: $surface;
    border: 1px solid $border;
    border-radius: 14px;
    padding: 18px 20px 14px;
    margin-bottom: 18px;
    box-shadow: 0 1px 2px rgba(15, 23, 42, 0.04);
}

.dash-header-top {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    gap: 16px;
    margin-bottom: 18px;
}

.dash-eyebrow {
    font-family: 'JetBrains Mono', monospace;
    font-size: 10px;
    font-weight: 600;
    color: $primary;
    letter-spacing: 0.14em;
    margin-bottom: 4px;

    &::before {
        content: '';
        display: inline-block;
        width: 6px;
        height: 6px;
        background: $cta;
        border-radius: 50%;
        margin-right: 6px;
        vertical-align: middle;
        animation: pulse 2s infinite;
    }
}

.dash-title {
    font-size: 22px;
    font-weight: 700;
    margin: 0;
    color: $text;
    letter-spacing: -0.01em;

    .dash-title-v {
        font-family: 'JetBrains Mono', monospace;
        font-size: 12px;
        font-weight: 600;
        color: $cta;
        background: rgba(249, 115, 22, 0.12);
        padding: 2px 6px;
        border-radius: 4px;
        vertical-align: middle;
        margin-left: 4px;
    }
}

.dash-header-actions {
    display: flex;
    gap: 10px;
    align-items: center;
}

.seg-control {
    display: inline-flex;
    background: $bg;
    border: 1px solid $border;
    border-radius: 8px;
    padding: 3px;
}

.seg-btn {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 6px 12px;
    font-size: 12px;
    font-weight: 500;
    color: $text-mute;
    background: transparent;
    border: 0;
    border-radius: 6px;
    cursor: pointer;
    transition: all 200ms ease;

    &:hover { color: $text; }

    &.active {
        background: $surface;
        color: $primary;
        box-shadow: 0 1px 2px rgba(15, 23, 42, 0.08);
    }

    &:focus-visible {
        outline: 2px solid $primary;
        outline-offset: 2px;
    }
}

.seg-btn-count {
    display: inline-block;
    font-family: 'JetBrains Mono', monospace;
    font-size: 10px;
    font-weight: 700;
    line-height: 1;
    padding: 3px 6px;
    border-radius: 10px;
    background: rgba(15, 23, 42, 0.06);
    color: $text-soft;
    margin-left: 2px;
    transition: all 200ms ease;
}

.seg-btn.active .seg-btn-count {
    background: rgba(59, 130, 246, 0.12);
    color: $primary;
}

// Estado pre-producción → variante amber para todo el header
.fabricando-v2.is-pre {
    .dash-eyebrow {
        color: $amber;
        &::before { background: $primary; }
    }
    .seg-btn.active {
        color: $amber;
        .seg-btn-count {
            background: rgba(245, 158, 11, 0.14);
            color: $amber;
        }
    }
    .filter-input:focus {
        border-color: $amber;
        box-shadow: 0 0 0 3px rgba(245, 158, 11, 0.12);
    }
    .filter-results-num { color: $amber; }
    .prod-card:hover {
        border-color: $amber-soft;
        .prod-qty-badge { background: $amber; color: $surface; }
        .prod-card-footer { color: $amber; background: rgba(245, 158, 11, 0.06); }
    }
    .prod-qty-badge {
        background: rgba(245, 158, 11, 0.12);
        color: $amber;
    }
    .qty-pill {
        color: $amber;
        background: rgba(245, 158, 11, 0.12);
    }
    .list-row:hover { background: rgba(245, 158, 11, 0.05); }
    .prod-table tbody tr:hover { background: rgba(245, 158, 11, 0.05); }
    .kpi-card:hover {
        border-color: $amber-soft;
        box-shadow: 0 2px 8px rgba(245, 158, 11, 0.1);
    }
    .dot-amber { background: $amber; }
}

.dot-amber { background: $amber; }

.btn-reload {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 7px 14px;
    font-size: 12px;
    font-weight: 600;
    color: $surface;
    background: $primary;
    border: 0;
    border-radius: 8px;
    cursor: pointer;
    transition: all 200ms ease;

    &:hover:not(:disabled) {
        background: darken($primary, 6%);
        box-shadow: 0 4px 12px rgba(59, 130, 246, 0.25);
    }
    &:disabled { opacity: 0.6; cursor: not-allowed; }
    &:focus-visible { outline: 2px solid $primary; outline-offset: 2px; }

    .spin { animation: spin 1s linear infinite; }
}

// ====== KPI STRIP ======
.kpi-strip {
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: 12px;
    margin-bottom: 16px;

    @media (max-width: 900px) { grid-template-columns: repeat(2, 1fr); }
    @media (max-width: 480px) { grid-template-columns: 1fr; }
}

.kpi-card {
    background: $bg;
    border: 1px solid $border;
    border-radius: 10px;
    padding: 12px 14px;
    transition: all 200ms ease;

    &:hover {
        border-color: $primary-soft;
        box-shadow: 0 2px 8px rgba(59, 130, 246, 0.08);
    }
}

.kpi-label {
    font-family: 'JetBrains Mono', monospace;
    font-size: 9.5px;
    font-weight: 600;
    color: $text-soft;
    letter-spacing: 0.12em;
    margin-bottom: 6px;
}

.kpi-value {
    font-family: 'JetBrains Mono', monospace;
    font-size: 26px;
    font-weight: 700;
    color: $text;
    line-height: 1.1;
    margin-bottom: 6px;
    display: flex;
    align-items: center;
    gap: 8px;
}

.kpi-value-sm { font-size: 16px; }

.kpi-foot {
    font-size: 10.5px;
    color: $text-mute;
    display: flex;
    align-items: center;
    gap: 6px;
}

.dot {
    display: inline-block;
    width: 6px;
    height: 6px;
    border-radius: 50%;
}
.dot-blue { background: $primary; }
.dot-orange { background: $cta; }
.dot-green { background: #10B981; }
.dot.pulse { animation: pulse 2s infinite; }

// ====== FILTERS BAR ======
.filters-bar {
    display: flex;
    gap: 12px;
    align-items: center;
    flex-wrap: wrap;
}

.filter-search {
    position: relative;
    flex: 1;
    min-width: 220px;
}

.filter-search-icon {
    position: absolute;
    left: 12px;
    top: 50%;
    transform: translateY(-50%);
    color: $text-soft;
    pointer-events: none;
}

.filter-input {
    width: 100%;
    height: 36px;
    padding: 0 36px 0 34px;
    background: $bg;
    border: 1px solid $border;
    border-radius: 8px;
    font-size: 13px;
    font-family: inherit;
    color: $text;
    transition: all 200ms ease;

    &::placeholder { color: $text-soft; }
    &:focus {
        outline: none;
        border-color: $primary;
        background: $surface;
        box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.12);
    }
}

.filter-clear {
    position: absolute;
    right: 8px;
    top: 50%;
    transform: translateY(-50%);
    width: 22px;
    height: 22px;
    border: 0;
    background: $border;
    color: $text-mute;
    border-radius: 50%;
    cursor: pointer;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    transition: all 200ms ease;

    &:hover { background: $cta; color: $surface; }
}

.filter-group {
    display: inline-flex;
    align-items: center;
    gap: 8px;
}

.filter-label {
    font-family: 'JetBrains Mono', monospace;
    font-size: 9.5px;
    font-weight: 600;
    color: $text-soft;
    letter-spacing: 0.1em;
    margin: 0;
}

.filter-select {
    height: 36px;
    padding: 0 28px 0 12px;
    background: $bg url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='10' height='10' viewBox='0 0 10 10'><path fill='%2364748B' d='M5 7L1 3h8z'/></svg>") no-repeat right 10px center;
    border: 1px solid $border;
    border-radius: 8px;
    font-size: 12px;
    font-family: inherit;
    color: $text;
    cursor: pointer;
    appearance: none;

    &:focus { outline: none; border-color: $primary; }
}

.filter-results {
    display: inline-flex;
    align-items: baseline;
    gap: 6px;
    margin-left: auto;
    padding: 7px 12px;
    background: $bg;
    border: 1px solid $border;
    border-radius: 8px;
    font-family: 'JetBrains Mono', monospace;
    font-size: 12px;
}
.filter-results-num { font-weight: 700; color: $primary; }
.filter-results-txt { color: $text-soft; font-size: 10.5px; }

// ====== GRID ======
.prod-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(230px, 1fr));
    gap: 14px;
}

.prod-card {
    position: relative;
    background: $surface;
    border: 1px solid $border;
    border-radius: 12px;
    overflow: hidden;
    cursor: pointer;
    display: flex;
    flex-direction: column;
    transition: all 200ms ease;

    &:hover {
        border-color: $primary-soft;
        box-shadow: 0 8px 24px rgba(15, 23, 42, 0.08);

        .prod-card-footer { color: $primary; background: rgba(59, 130, 246, 0.06); }
        .prod-qty-badge { background: $primary; color: $surface; }
    }

    &:focus-visible {
        outline: 2px solid $primary;
        outline-offset: 2px;
    }
}

.prod-card--negative-stock {
    border-color: rgba(249, 115, 22, 0.55);
    box-shadow: 0 0 0 1px rgba(249, 115, 22, 0.16);
}

.prod-card--negative-stock:hover {
    border-color: #f97316;
    box-shadow: 0 10px 26px rgba(249, 115, 22, 0.16);
}

.prod-card-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 8px 10px 6px;
    background: $surface;
}

.prod-idx {
    font-family: 'JetBrains Mono', monospace;
    font-size: 10px;
    font-weight: 600;
    color: $text-soft;
}

.prod-qty-badge {
    display: inline-flex;
    align-items: baseline;
    gap: 3px;
    font-family: 'JetBrains Mono', monospace;
    background: rgba(59, 130, 246, 0.1);
    color: $primary;
    padding: 3px 8px;
    border-radius: 6px;
    transition: all 200ms ease;
}
.prod-qty-num { font-size: 13px; font-weight: 700; }
.prod-qty-uds { font-size: 8.5px; font-weight: 600; letter-spacing: 0.05em; }

.prod-card-img {
    position: relative;
    width: 100%;
    aspect-ratio: 16 / 11;
    background: $surface;
    overflow: hidden;
    border-top: 1px solid rgba(15, 23, 42, 0.05);
    border-bottom: 1px solid rgba(15, 23, 42, 0.05);

    img {
        position: absolute;
        inset: 0;
        width: 100%;
        height: 100%;
        object-fit: contain;
        object-position: center;
        display: block;
    }
}

.prod-img-placeholder {
    color: #cbd5e1;
}

.prod-card-body {
    padding: 12px 14px 10px;
    flex: 1;
}

.prod-name {
    font-size: 13px;
    font-weight: 600;
    line-height: 1.35;
    margin: 0 0 10px;
    color: $text;
    text-align: center;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
    min-height: 35px;
}

.prod-meta {
    display: flex;
    flex-direction: column;
    gap: 5px;
}

.meta-row {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 10px;
    font-size: 11px;

    .meta-k {
        font-family: 'JetBrains Mono', monospace;
        color: $text-soft;
        font-size: 9.5px;
        font-weight: 600;
        letter-spacing: 0.08em;
        flex-shrink: 0;
    }
    .meta-v {
        color: $text-mute;
        font-weight: 500;
        text-align: right;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
        display: inline-flex;
        align-items: center;
        gap: 6px;
        min-width: 0;
    }
    .meta-mono {
        font-family: 'JetBrains Mono', monospace;
        color: $text;
        font-size: 10.5px;
    }
}

.meta-v.sku-sm { font-size: 9.5px !important; }
.meta-v.sku-xs { font-size: 9px !important; }
.meta-v.sku-xxs { font-size: 8.5px !important; }

.eta-badge {
    display: inline-flex;
    align-items: center;
    min-height: 24px;
    padding: 4px 8px;
    border-radius: 999px;
    font-family: 'JetBrains Mono', monospace;
    font-size: 10px;
    font-weight: 600;
    line-height: 1;
    white-space: nowrap;
}

.eta-badge--set {
    background: rgba(59, 130, 246, 0.1);
    color: #1d4ed8;
    border: 1px solid rgba(59, 130, 246, 0.18);
}

.eta-badge--missing {
    background: rgba(245, 158, 11, 0.12);
    color: #b45309;
    border: 1px solid rgba(245, 158, 11, 0.2);
}

.eta-badge--paused {
    background: rgba(239, 68, 68, 0.12);
    color: #b91c1c;
    border: 1px solid rgba(239, 68, 68, 0.2);
}

.stock-badge {
    display: inline-flex;
    align-items: center;
    min-height: 24px;
    padding: 4px 8px;
    border-radius: 999px;
    font-family: 'JetBrains Mono', monospace;
    font-size: 10px;
    font-weight: 700;
    line-height: 1;
    white-space: nowrap;
    border: 1px solid transparent;
}

.stock-badge--positive {
    background: rgba(16, 185, 129, 0.12);
    color: #047857;
    border-color: rgba(16, 185, 129, 0.2);
}

.stock-badge--zero {
    background: rgba(148, 163, 184, 0.14);
    color: #475569;
    border-color: rgba(148, 163, 184, 0.2);
}

.stock-badge--negative {
    background: rgba(249, 115, 22, 0.12);
    color: #c2410c;
    border-color: rgba(249, 115, 22, 0.24);
}

.color-chip {
    display: inline-block;
    width: 10px;
    height: 10px;
    border-radius: 50%;
    border: 1px solid rgba(15, 23, 42, 0.12);
    flex-shrink: 0;

    &.lg { width: 14px; height: 14px; }
}

.prod-card-footer {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 8px 14px;
    border-top: 1px solid $border;
    font-family: 'JetBrains Mono', monospace;
    font-size: 10px;
    font-weight: 600;
    color: $text-soft;
    letter-spacing: 0.06em;
    text-transform: uppercase;
    transition: all 200ms ease;
}

.btn-preorder {
    width: 26px; height: 26px; border-radius: 6px; border: 1px solid $border; background: transparent;
    color: $text-soft; cursor: pointer; display: flex; align-items: center; justify-content: center;
    font-size: 11px; transition: all 150ms;
    &:hover { background: rgba($primary, 0.08); color: $primary; border-color: rgba($primary, 0.3); }
    &--table { width: 24px; height: 24px; font-size: 10px; margin-right: 6px; }
    &--active { background: rgba(16, 185, 129, 0.1); color: #10B981; border-color: rgba(16, 185, 129, 0.3); &:hover { background: rgba(16, 185, 129, 0.18); color: #059669; } }
}

.td-actions { display: flex; align-items: center; justify-content: flex-end; gap: 4px; }

// Pre-order modal
.preorder-modal { padding: 4px 0; }
.preorder-info { display: flex; align-items: center; gap: 10px; margin-bottom: 16px; }
.preorder-sku { font-family: 'JetBrains Mono', monospace; font-size: 12px; font-weight: 600; color: $primary; background: rgba($primary, 0.06); padding: 3px 8px; border-radius: 4px; }
.preorder-name { font-family: 'Plus Jakarta Sans', sans-serif; font-size: 14px; font-weight: 600; color: $text; }
.preorder-field { margin-bottom: 14px; }
.preorder-label { display: block; font-family: 'JetBrains Mono', monospace; font-size: 10px; font-weight: 600; letter-spacing: 0.5px; text-transform: uppercase; color: $text-soft; margin-bottom: 6px; code { background: rgba($primary, 0.06); padding: 1px 5px; border-radius: 3px; font-size: 10px; color: $primary; } }
.preorder-input { width: 100%; padding: 10px 14px; border: 1px solid $border; border-radius: 8px; font-family: 'Plus Jakarta Sans', sans-serif; font-size: 14px; color: $text; &:focus { outline: none; border-color: $primary; box-shadow: 0 0 0 3px rgba($primary, 0.1); } &::placeholder { color: #CBD5E1; } }
.preorder-preview { margin-bottom: 16px; padding: 10px 14px; background: $bg; border: 1px solid $border; border-radius: 8px; }
.preorder-preview-label { display: block; font-family: 'JetBrains Mono', monospace; font-size: 9px; font-weight: 600; text-transform: uppercase; letter-spacing: 0.5px; color: $text-soft; margin-bottom: 4px; }
.preorder-preview-text { font-family: 'Plus Jakarta Sans', sans-serif; font-size: 14px; font-weight: 600; color: $text; }
.preorder-actions { display: flex; justify-content: flex-end; gap: 8px; }
.btn-preorder-cancel { padding: 8px 16px; border-radius: 8px; border: none; background: transparent; color: $text-soft; font-family: 'Plus Jakarta Sans', sans-serif; font-size: 13px; font-weight: 600; cursor: pointer; &:hover { background: $bg; color: $text; } }
.btn-preorder-clear { padding: 8px 16px; border-radius: 8px; border: 1px solid rgba(239, 68, 68, 0.3); background: transparent; color: #EF4444; font-family: 'Plus Jakarta Sans', sans-serif; font-size: 13px; font-weight: 600; cursor: pointer; display: flex; align-items: center; gap: 5px; &:hover { background: rgba(239, 68, 68, 0.05); } &:disabled { opacity: 0.5; cursor: not-allowed; } }
.btn-preorder-save { padding: 8px 16px; border-radius: 8px; border: none; background: $primary; color: white; font-family: 'Plus Jakarta Sans', sans-serif; font-size: 13px; font-weight: 600; cursor: pointer; display: flex; align-items: center; gap: 5px; box-shadow: 0 2px 6px rgba(59, 130, 246, 0.25); &:hover { background: darken($primary, 8%); } &:disabled { opacity: 0.5; cursor: not-allowed; } }

// ====== LIST VIEW ======
.list-wrap {
    background: $surface;
    border: 1px solid $border;
    border-radius: 12px;
    overflow: hidden;
}
.list-table {
    width: 100%;
    border-collapse: collapse;
    font-size: 13px;

    thead th {
        font-family: 'JetBrains Mono', monospace;
        font-size: 10px;
        font-weight: 600;
        color: $text-soft;
        letter-spacing: 0.08em;
        text-align: left;
        padding: 14px 16px;
        background: $bg;
        border-bottom: 1px solid $border;
        white-space: nowrap;
    }

    tbody tr {
        cursor: pointer;
        transition: background 150ms ease;
        border-bottom: 1px solid $border;
        &:last-child { border-bottom: 0; }
        &:hover { background: rgba(59, 130, 246, 0.04); }
        &:focus-visible { outline: 2px solid $primary; outline-offset: -2px; }
    }

    .lt-selected { background: rgba(59, 130, 246, 0.06); }
    .tr-negative-stock { border-left: 3px solid rgba(249, 115, 22, 0.55); }

    td { padding: 14px 16px; color: $text; vertical-align: middle; }
}
.lt-check { width: 40px; text-align: center; }
.lt-img { width: 48px; }
.lt-center { text-align: center; }
.lt-right { text-align: right; }
.list-checkbox { width: 16px; height: 16px; cursor: pointer; accent-color: $primary; }
.btn-hist {
    width: 24px; height: 24px; border-radius: 6px; border: 1px solid $border; background: transparent;
    color: $text-soft; cursor: pointer; display: inline-flex; align-items: center; justify-content: center;
    transition: all 150ms; margin-right: 6px;
    &:hover { background: rgba($primary, 0.08); color: $primary; border-color: rgba($primary, 0.3); }
}

// ====== FLOATING ACTION BAR ======
.fab-bar {
    position: fixed;
    bottom: 24px;
    left: 50%;
    transform: translateX(-50%);
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 20px;
    padding: 10px 16px 10px 20px;
    background: $text;
    color: $surface;
    border-radius: 12px;
    box-shadow: 0 8px 32px rgba(15, 23, 42, 0.28);
    z-index: 100;
    min-width: 420px;
    font-family: 'Plus Jakarta Sans', sans-serif;
}
.fab-bar-left { display: flex; align-items: center; gap: 8px; }
.fab-count { font-family: 'JetBrains Mono', monospace; font-size: 16px; font-weight: 700; color: $primary-soft; }
.fab-label { font-size: 13px; font-weight: 500; color: rgba(255, 255, 255, 0.7); }
.fab-clear {
    display: inline-flex; align-items: center; gap: 4px; padding: 4px 10px;
    background: rgba(255, 255, 255, 0.1); border: 0; border-radius: 6px;
    color: rgba(255, 255, 255, 0.7); font-size: 11px; font-weight: 500; cursor: pointer;
    &:hover { background: rgba(255, 255, 255, 0.18); color: #fff; }
}
.fab-action {
    display: inline-flex; align-items: center; gap: 6px; padding: 8px 16px;
    background: $primary; border: 0; border-radius: 8px; color: #fff;
    font-size: 13px; font-weight: 600; cursor: pointer; transition: all 150ms;
    &:hover { background: darken($primary, 8%); box-shadow: 0 4px 12px rgba(59, 130, 246, 0.3); }
}
.fab-slide-enter-active, .fab-slide-leave-active { transition: all 300ms cubic-bezier(0.16, 1, 0.3, 1); }
.fab-slide-enter, .fab-slide-leave-to { opacity: 0; transform: translateX(-50%) translateY(20px); }

// ====== TABLE ======
.table-wrap {
    background: $surface;
    border: 1px solid $border;
    border-radius: 12px;
    overflow: hidden;
}

.prod-table {
    width: 100%;
    border-collapse: collapse;
    font-size: 13px;

    thead th {
        font-family: 'JetBrains Mono', monospace;
        font-size: 10px;
        font-weight: 600;
        color: $text-soft;
        letter-spacing: 0.08em;
        text-align: left;
        padding: 14px 16px;
        background: $bg;
        border-bottom: 1px solid $border;
        white-space: nowrap;
    }

    tbody tr {
        cursor: pointer;
        transition: background 150ms ease;
        border-bottom: 1px solid $border;

        &:last-child { border-bottom: 0; }
        &:hover { background: rgba(59, 130, 246, 0.04); }
        &:focus-visible {
            outline: 2px solid $primary;
            outline-offset: -2px;
        }
    }

    .tr-negative-stock {
        border-left: 3px solid rgba(249, 115, 22, 0.55);
    }

    td {
        padding: 14px 16px;
        color: $text;
        vertical-align: middle;
    }
}

.th-img, .td-img { width: 48px; }
.th-right, .td-right { text-align: right; }
.th-center, .td-center { text-align: center; }

.td-img img, .td-img-ph {
    width: 36px;
    height: 36px;
    border-radius: 6px;
    object-fit: cover;
    background: $bg;
    display: flex;
    align-items: center;
    justify-content: center;
    color: $text-soft;
}

.td-name { font-weight: 500; max-width: 260px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.td-sku { color: $text-mute; font-size: 11.5px; letter-spacing: 0.02em; }
.td-color { white-space: nowrap; }
.td-color-txt { margin-left: 6px; font-size: 12px; color: $text-mute; text-transform: uppercase; }
.td-date { color: $text-mute; font-size: 11.5px; }
.td-chev { color: $text-soft; }

.qty-pill {
    display: inline-block;
    font-family: 'JetBrains Mono', monospace;
    font-size: 12px;
    font-weight: 700;
    color: $primary;
    background: rgba(59, 130, 246, 0.1);
    padding: 3px 10px;
    border-radius: 12px;
}

// ====== SKELETON ======
.skeleton-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(230px, 1fr));
    gap: 14px;
}

.skeleton-card {
    background: $surface;
    border: 1px solid $border;
    border-radius: 12px;
    padding: 14px;
}

.skeleton-img {
    width: 100%;
    aspect-ratio: 4 / 3;
    background: linear-gradient(90deg, #f1f5f9 25%, #e2e8f0 50%, #f1f5f9 75%);
    background-size: 200% 100%;
    border-radius: 8px;
    margin-bottom: 12px;
    animation: shimmer 1.4s infinite;
}

.skeleton-line {
    height: 10px;
    background: linear-gradient(90deg, #f1f5f9 25%, #e2e8f0 50%, #f1f5f9 75%);
    background-size: 200% 100%;
    border-radius: 4px;
    margin-bottom: 8px;
    animation: shimmer 1.4s infinite;

    &.w-80 { width: 80%; }
    &.w-60 { width: 60%; }
    &.w-40 { width: 40%; }
}

// ====== EMPTY ======
.empty-state {
    background: $surface;
    border: 1px dashed $border;
    border-radius: 12px;
    padding: 40px 20px;
    text-align: center;
    color: $text-mute;

    svg { color: $text-soft; margin-bottom: 10px; }
    p { margin: 0 0 14px; font-size: 13px; }

    &.empty-success svg { color: #10B981; }
}

// ====== MODAL HISTORIAL ======
.hist-loading { text-align: center; padding: 24px 0; }
.hist-empty { text-align: center; padding: 24px 0; color: $text-mute; }

.hist-list {
    display: flex;
    flex-direction: column;
}

.hist-notas {
    display: flex;
    flex-direction: column;
    gap: 8px;
    padding: 10px 12px;
    margin-bottom: 14px;
    background: rgba(234, 84, 85, 0.06);
    border: 1px solid rgba(234, 84, 85, 0.18);
    border-radius: 8px;
}
.hist-notas-title {
    font-family: 'JetBrains Mono', monospace;
    font-size: 10px;
    font-weight: 700;
    letter-spacing: 0.08em;
    color: #b91c1c;
    text-transform: uppercase;
    display: flex;
    align-items: center;
    gap: 6px;
}
.hist-nota {
    padding: 8px 10px;
    background: #fff;
    border-radius: 6px;
    border-left: 3px solid #ea5455;
}
.hist-nota-head {
    display: flex;
    align-items: center;
    margin-bottom: 4px;
    gap: 10px;
    .hist-fecha { margin-left: auto; }
}
.hist-nota-text {
    font-size: 12px;
    color: $text-mute;
    line-height: 1.35;
    word-break: break-word;
    display: flex;
    align-items: center;
    gap: 6px;
    margin-top: 2px;
    i { font-size: 11px; color: $text-soft; }
}

.hist-item {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 12px 4px;
    border-bottom: 1px solid $border;

    &:last-child { border-bottom: 0; }
}

.hist-item-left, .hist-item-right {
    display: flex;
    align-items: center;
    gap: 10px;
}

.hist-tag {
    font-family: 'JetBrains Mono', monospace;
    font-size: 9.5px;
    font-weight: 700;
    letter-spacing: 0.08em;
    padding: 3px 8px;
    border-radius: 4px;

    &.tag-cut { color: #0284C7; background: rgba(2, 132, 199, 0.12); }
    &.tag-stitch { color: #B45309; background: rgba(180, 83, 9, 0.12); }
    &.tag-pause { color: #b91c1c; background: rgba(239, 68, 68, 0.12); }
    &.tag-resume { color: #047857; background: rgba(16, 185, 129, 0.12); }
}

.hist-pedido { font-size: 12px; color: $text; font-weight: 500; }
.hist-qty {
    font-family: 'JetBrains Mono', monospace;
    font-size: 13px;
    font-weight: 700;
    color: #10B981;
}
.hist-fecha { font-size: 11px; color: $text-soft; }

// ====== ANIMATIONS ======
@keyframes spin { to { transform: rotate(360deg); } }
@keyframes pulse {
    0%, 100% { opacity: 1; transform: scale(1); }
    50% { opacity: 0.55; transform: scale(0.85); }
}
@keyframes shimmer {
    0% { background-position: 200% 0; }
    100% { background-position: -200% 0; }
}

@media (prefers-reduced-motion: reduce) {
    *, *::before, *::after {
        animation-duration: 0.01ms !important;
        transition-duration: 0.01ms !important;
    }
}

// ====== DARK MODE ======
.dark-layout {
    .fabricando-v2 { color: #d0d2d6; }

    .dash-header,
    .table-wrap,
    .prod-card,
    .skeleton-card,
    .empty-state {
        background: #242424;
        border-color: #3b3b3b;
    }

    .dash-title { color: #e4e6eb; }
    .kpi-card {
        background: #1f1f1f;
        border-color: #3b3b3b;
        &:hover { border-color: $primary; }
    }
    .kpi-value { color: #e4e6eb; }
    .kpi-label, .filter-label, .meta-k, .prod-idx, .filter-results-txt { color: #6e7079; }

    .filter-input,
    .filter-select,
    .filter-results {
        background: #1f1f1f;
        border-color: #3b3b3b;
        color: #d0d2d6;
    }
    .filter-input:focus { background: #2a2a2a; }

    .seg-control { background: #1f1f1f; border-color: #3b3b3b; }
    .seg-btn.active { background: #2c2c2c; }

    .prod-card-img {
        background: linear-gradient(145deg, #2a2a2a, #1f1f1f);
        border-bottom-color: #3b3b3b;
    }
    .prod-img-placeholder, .td-img-ph { color: #4a4a4a; }
    .prod-name { color: #e4e6eb; }
    .meta-v { color: #b4b7bd; }
    .meta-mono { color: #d0d2d6; }
    .prod-card-header { background: #242424; }
    .prod-card-footer { border-top-color: #3b3b3b; }

    .prod-table thead th { background: #1f1f1f; border-bottom-color: #3b3b3b; color: #6e7079; }
    .prod-table td { color: #d0d2d6; }
    .prod-table tbody tr { border-bottom-color: #3b3b3b; }
    .prod-table tbody tr:hover { background: rgba(59, 130, 246, 0.08); }
    .td-sku, .td-date, .td-color-txt { color: #b4b7bd; }
    .eta-badge--set { color: #93c5fd; background: rgba(59, 130, 246, 0.14); border-color: rgba(96, 165, 250, 0.26); }
    .eta-badge--missing { color: #fbbf24; background: rgba(245, 158, 11, 0.14); border-color: rgba(251, 191, 36, 0.2); }
    .eta-badge--paused { color: #fca5a5; background: rgba(239, 68, 68, 0.16); border-color: rgba(248, 113, 113, 0.24); }
    .stock-badge--positive { color: #6ee7b7; background: rgba(16, 185, 129, 0.16); border-color: rgba(52, 211, 153, 0.24); }
    .stock-badge--zero { color: #cbd5e1; background: rgba(148, 163, 184, 0.14); border-color: rgba(148, 163, 184, 0.2); }
    .stock-badge--negative { color: #fdba74; background: rgba(249, 115, 22, 0.18); border-color: rgba(251, 146, 60, 0.26); }
    .prod-card--negative-stock { border-color: rgba(249, 115, 22, 0.5); box-shadow: 0 0 0 1px rgba(249, 115, 22, 0.18); }

    .list-wrap { background: #242424; border-color: #3b3b3b; }
    .list-header { background: #1f1f1f; border-bottom-color: #3b3b3b; color: #6e7079; }
    .list-row { border-bottom-color: #3b3b3b; }
    .list-row:hover { background: rgba(59, 130, 246, 0.08); }
    .list-row--selected { background: rgba(59, 130, 246, 0.1); }
    .list-product-name { color: #e4e6eb; }
    .list-product-sku { color: #b4b7bd; }
    .fab-bar { background: #2a2a2a; box-shadow: 0 8px 32px rgba(0, 0, 0, 0.5); }

    .hist-item { border-bottom-color: #3b3b3b; }
    .hist-pedido { color: #d0d2d6; }
}
</style>
